import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftSnackbar from "components/SoftSnackbar";
import SoftButton from "components/SoftButton";
import Card from "@mui/material/Card";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Icon from "@mui/material/Icon";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DataTable from "components/QuestDataTable";
import { API_URL } from "config";
import { useAuth } from "../../../AuthContext";
import { useModulePermissions } from "utils/modulePermissions";

const DepartmentList = () => {
  const navigate = useNavigate();
  const { isAuthReady, permissions = [], userPermission = [] } = useAuth();
  const { hasModulePermission, canViewField } = useModulePermissions(
    permissions,
    userPermission,
    "department"
  );

  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", color: "success" });
  const [deletingId, setDeletingId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState(null);

  const canViewPage = hasModulePermission("department.pages.list.view");
  const canCreate =
    hasModulePermission("department.pages.create.view") &&
    hasModulePermission("department.actions.open_create");
  const canOpenTrashed =
    hasModulePermission("department.pages.trashed.view") &&
    hasModulePermission("department.actions.open_trashed");
  const canOpenEdit =
    hasModulePermission("department.pages.edit.view") &&
    hasModulePermission("department.actions.open_edit");
  const canDelete = hasModulePermission("department.actions.delete");
  const canConfirmDelete = hasModulePermission("department.actions.confirm_delete");
  const canCancelDelete = hasModulePermission("department.actions.cancel_delete");

  const closeSnackbar = () => setSnackbar((prev) => ({ ...prev, open: false }));

  const fetchDepartments = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${API_URL}/departments`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Failed to fetch departments");
      }
      const { data } = await res.json();
      setDepartments(data || []);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || "Error loading departments",
        color: "error",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthReady) return;
    if (!canViewPage) {
      setLoading(false);
      return;
    }
    fetchDepartments();
  }, [canViewPage, fetchDepartments, isAuthReady]);

  const handleDeleteClick = (department) => {
    if (!canDelete) {
      setSnackbar({
        open: true,
        message: "You do not have permission to delete departments.",
        color: "error",
      });
      return;
    }
    setSelectedDepartment(department);
    setDeleteDialogOpen(true);
  };

  const handleEditClick = (department) => {
    if (!canOpenEdit) {
      setSnackbar({
        open: true,
        message: "You do not have permission to edit departments.",
        color: "error",
      });
      return;
    }
    navigate(`/departments/edit/${department.id}`);
  };

  const handleDeleteConfirm = async () => {
    if (!canDelete || !canConfirmDelete) {
      setSnackbar({
        open: true,
        message: "You do not have permission to confirm delete.",
        color: "error",
      });
      return;
    }

    if (!selectedDepartment) return;

    try {
      setDeletingId(selectedDepartment.id);
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${API_URL}/departments/${selectedDepartment.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Delete failed");
      }
      const data = await res.json();
      setSnackbar({
        open: true,
        message: data.message || "Department deleted successfully",
        color: "success",
      });
      fetchDepartments();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || "Delete failed",
        color: "error",
      });
    } finally {
      setDeletingId(null);
      setDeleteDialogOpen(false);
      setSelectedDepartment(null);
    }
  };

  const closeDeleteDialog = () => {
    if (!canCancelDelete || deletingId) return;
    setDeleteDialogOpen(false);
    setSelectedDepartment(null);
  };

  const columns = useMemo(() => {
    const visibleColumns = [];
    if (canViewField("id")) visibleColumns.push({ Header: "ID", accessor: "id", align: "left" });
    if (canViewField("name")) visibleColumns.push({ Header: "Name", accessor: "name", align: "left" });
    if (canViewField("created_at")) {
      visibleColumns.push({ Header: "Created At", accessor: "created_at", align: "left" });
    }
    if (canViewField("status")) visibleColumns.push({ Header: "Status", accessor: "status", align: "center" });
    if (canViewField("actions") && (canOpenEdit || canDelete)) {
      visibleColumns.push({ Header: "Actions", accessor: "actions", align: "center", disableSortBy: true });
    }
    return visibleColumns;
  }, [canDelete, canOpenEdit, canViewField]);

  const rows = useMemo(
    () =>
      departments.map((dept) => ({
        id: dept.id,
        name: dept.name,
        created_at: dept.created_at ? new Date(dept.created_at).toLocaleString() : "-",
        status: (
          <SoftBox display="inline-flex" alignItems="center" px={1.5} py={0.5} borderRadius={1}>
            <SoftTypography variant="caption" color={dept.status ? "info" : "error"}>
              {dept.status ? "ACTIVE" : "INACTIVE"}
            </SoftTypography>
          </SoftBox>
        ),
        actions: (
          <SoftBox display="flex" justifyContent="center">
            {canOpenEdit && (
              <Tooltip title="Edit Department">
                <IconButton size="small" onClick={() => handleEditClick(dept)} aria-label="edit">
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {canDelete && (
              <Tooltip title="Delete Department">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleDeleteClick(dept)}
                  aria-label="delete"
                  disabled={deletingId === dept.id}
                >
                  {deletingId === dept.id ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <DeleteIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            )}
          </SoftBox>
        ),
      })),
    [canDelete, canOpenEdit, deletingId, departments]
  );

  if (!isAuthReady || (canViewPage && loading)) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox display="flex" justifyContent="center" alignItems="center" height="200px">
          <CircularProgress size={50} color="info" />
        </SoftBox>
      </DashboardLayout>
    );
  }

  if (!canViewPage) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox p={3}>
          <SoftTypography variant="h5" fontWeight="bold">
            Department List
          </SoftTypography>
          <SoftTypography variant="body2" color="text" mt={1}>
            You do not have permission to view departments.
          </SoftTypography>
        </SoftBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <SoftSnackbar
        color={snackbar.color}
        icon="notifications"
        title="Department Management"
        content={snackbar.message}
        open={snackbar.open}
        close={closeSnackbar}
        onClose={closeSnackbar}
        time={3000}
      />

      <SoftBox p={3}>
        <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <SoftTypography variant="h4" fontWeight="bold">
            Department List
          </SoftTypography>
          <SoftBox display="flex" gap={1} flexWrap="wrap" justifyContent="flex-end">
            {canOpenTrashed && (
              <SoftButton
                size="small"
                color="warning"
                onClick={() => navigate("/departments/trashed")}
                startIcon={<Icon>delete</Icon>}
              >
                Trash
              </SoftButton>
            )}
            {canCreate && (
              <SoftButton
                size="small"
                color="info"
                startIcon={<Icon>add</Icon>}
                onClick={() => navigate("/departments/add")}
              >
                Add New Department
              </SoftButton>
            )}
          </SoftBox>
        </SoftBox>

        <Card>
          {columns.length > 0 ? (
            <DataTable table={{ columns, rows }} />
          ) : (
            <SoftBox p={3}>
              <SoftTypography variant="body2" color="text">
                No visible fields are assigned for this page.
              </SoftTypography>
            </SoftBox>
          )}
        </Card>
      </SoftBox>

      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <SoftTypography variant="body2">
            Are you sure you want to delete the department{" "}
            <strong>{selectedDepartment?.name}</strong>?
          </SoftTypography>
        </DialogContent>
        <DialogActions>
          {canConfirmDelete && (
            <SoftButton
              color="info"
              variant="contained"
              onClick={handleDeleteConfirm}
              disabled={deletingId === selectedDepartment?.id}
              sx={{
                backgroundColor: "rgb(23, 193, 232)",
                "&:hover": { backgroundColor: "rgba(23, 193, 232, 0.8)" },
                color: "#fff",
                minWidth: 100,
              }}
            >
              {deletingId === selectedDepartment?.id ? "Processing..." : "Confirm"}
            </SoftButton>
          )}
          {canCancelDelete && (
            <SoftButton
              variant="contained"
              onClick={closeDeleteDialog}
              disabled={Boolean(deletingId)}
              sx={{
                backgroundColor: "red",
                "&:hover": { backgroundColor: "darkred" },
                color: "#fff",
                minWidth: 100,
              }}
            >
              Cancel
            </SoftButton>
          )}
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default DepartmentList;
